// Renders a menu name alongside the AI translations the restaurant owner chose
// to publish.
//
// English is always primary. The owner picks a second and third menu language
// in the admin Menus workspace and separately decides which of them customers
// see (restaurant.menuLanguageDisplay):
//
//   NONE   -> English only (the default; every restaurant starts here)
//   SECOND -> English + alternateName1
//   THIRD  -> English + alternateName2
//   BOTH   -> English + both
//
// A restaurant that never configured languages renders exactly as it did
// before this feature existed — no brackets, no empty parens.

// The translations to show, in display order. Empty array when nothing should
// be shown, so callers can skip rendering entirely.
export function menuNameAlternates(alt1, alt2, displayMode) {
  if (!displayMode || displayMode === 'NONE') return []
  const first = typeof alt1 === 'string' ? alt1.trim() : ''
  const second = typeof alt2 === 'string' ? alt2.trim() : ''

  const picked = []
  if (displayMode === 'SECOND' || displayMode === 'BOTH') picked.push(first)
  if (displayMode === 'THIRD' || displayMode === 'BOTH') picked.push(second)

  // Drop blanks (item not translated yet) and anything identical to another
  // entry, so a name that reads the same in two languages isn't doubled up.
  return picked.filter((v, i) => v && picked.indexOf(v) === i)
}

// Single-string form: "Chicken Biryani (చికెన్ బిర్యానీ) (鸡肉比尔亚尼)".
// Use where a plain string is required (alt text, aria labels, page titles).
export function formatMenuName(name, alt1, alt2, displayMode) {
  const base = name || ''
  const alternates = menuNameAlternates(alt1, alt2, displayMode)
  if (!alternates.length) return base
  return `${base} ${alternates.map((a) => `(${a})`).join(' ')}`
}

// True when this name (in English or any of its translations, shown or not)
// matches the search text. Search deliberately looks at every stored
// translation, not just the published ones — someone typing తెలుగు should find
// the dish even if the owner is currently showing Chinese.
export function menuNameMatches(query, name, alt1, alt2) {
  const q = (query || '').trim().toLowerCase()
  if (!q) return false
  return [name, alt1, alt2].some((v) => typeof v === 'string' && v.toLowerCase().includes(q))
}
